import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Brand } from '../components/Layout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }));
  };

  const handleOtpChange = (e) => {
    const numericValue = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(numericValue);
    if (fieldErrors.otp) setFieldErrors(prev => ({ ...prev, otp: '' }));
  };

  const handlePasswordChange = (e) => {
    setNewPassword(e.target.value);
    if (fieldErrors.newPassword) setFieldErrors(prev => ({ ...prev, newPassword: '' }));
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFieldErrors({ email: 'Please enter a valid email address.' });
      return;
    }
    setFieldErrors({});
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/auth/forgotpassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(data.message);
        setStep(2);
      } else {
        setError(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!otp.trim() || otp.length !== 6) {
      newErrors.otp = 'Please enter a valid 6-digit OTP.';
    }
    if (!newPassword || !/^(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/.test(newPassword)) {
      newErrors.newPassword = 'Password must be at least 8 chars long, include 1 uppercase, 1 digit, and 1 special character.';
    }
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      return;
    }

    setFieldErrors({});
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/auth/resetpassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage('Password reset successful! Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(data.message || 'Failed to reset password');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen grid place-items-center relative overflow-hidden bg-gradient-to-b from-primary-dark via-primary to-primary-deep p-4 text-white">
      {/* Animated Elements */}
      <div className="absolute inset-0 opacity-40 bg-[radial-gradient(rgba(192,132,252,0.8)_1.5px,transparent_1.5px)] [background-size:28px_28px] animate-grid-pulse pointer-events-none" />
      {/* <div className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-accent-bright to-transparent shadow-[0_0_20px_var(--color-accent-bright)] animate-laser pointer-events-none z-10" /> */}
      <div className="absolute -top-20 -left-20 w-[420px] h-[420px] rounded-full bg-accent-bright/15 blur-3xl float-slow pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-[420px] h-[420px] rounded-full bg-cta/25 blur-3xl float-medium pointer-events-none" />

      {/* GLASS CARD */}
      <div className="relative z-20 w-full max-w-[440px] glass-dark rounded-[32px] p-8 md:p-10 shadow-2xl border-2 border-white/20 animate-rise">
        <div className="mb-8 pb-4 border-b border-white/15">
          <Brand light={true} />
        </div>

        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Reset Password</h1>
          <p className="text-xs text-white/70 mt-1 font-medium">Follow the steps to regain access to your portal.</p>
        </div>

        {error && (
          <div className="bg-error/20 text-red-300 border border-error/40 p-3.5 rounded-xl text-xs font-semibold backdrop-blur-sm mb-6">
            {error}
          </div>
        )}
        {message && (
          <div className="bg-green-500/20 text-green-300 border border-green-500/40 p-3.5 rounded-xl text-xs font-semibold backdrop-blur-sm mb-6">
            {message}
          </div>
        )}

        {step === 1 && (
          <form onSubmit={handleSendOtp} className="grid gap-6" noValidate>
            <label className="grid gap-1.5 text-[10px] font-extrabold text-accent-bright tracking-wider uppercase">
              <span>ADMIN EMAIL <span className="text-red-400">*</span></span>
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter admin email"
                className={`w-full px-4 py-3.5 rounded-xl border bg-white/10 text-xs md:text-sm text-white placeholder-white/50 outline-none transition-all font-medium ${fieldErrors.email ? 'border-red-400 ring-2 ring-red-500/20' : 'border-white/20 focus:border-accent focus:ring-4 focus:ring-accent/20'
                  }`}
              />
              {fieldErrors.email && <span className="text-[11px] font-bold text-red-300 normal-case">{fieldErrors.email}</span>}
            </label>
            <button disabled={loading} className="btn-flash mt-2 w-full justify-center bg-cta hover:bg-cta-hover text-white font-extrabold text-sm py-4 px-6 rounded-2xl flex items-center gap-2 glow-cta transition-all border-0 shadow-lg cursor-pointer disabled:opacity-70 disabled:cursor-wait" type="submit">
              {loading ? 'Sending OTP...' : 'Send OTP to Email'} <span>→</span>
            </button>
            <Link to="/login" className="text-center text-xs text-white/70 hover:text-white mt-2 transition-colors">Back to Login</Link>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleResetPassword} className="grid gap-6" noValidate>
            <label className="grid gap-1.5 text-[10px] font-extrabold text-accent-bright tracking-wider uppercase">
              <span>6-DIGIT OTP <span className="text-red-400">*</span></span>
              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={handleOtpChange}
                placeholder="123456"
                className={`w-full px-4 py-3.5 rounded-xl border bg-white/10 text-xs md:text-sm text-white placeholder-white/50 outline-none transition-all font-medium text-center tracking-widest ${fieldErrors.otp ? 'border-red-400 ring-2 ring-red-500/20' : 'border-white/20 focus:border-accent focus:ring-4 focus:ring-accent/20'
                  }`}
              />
              {fieldErrors.otp && <span className="text-[11px] font-bold text-red-300 normal-case">{fieldErrors.otp}</span>}
            </label>
            <label className="grid gap-1.5 text-[10px] font-extrabold text-accent-bright tracking-wider uppercase">
              <span>NEW PASSWORD <span className="text-red-400">*</span></span>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  className={`w-full pl-4 pr-12 py-3.5 rounded-xl border bg-white/10 text-xs md:text-sm text-white placeholder-white/50 outline-none transition-all font-medium ${fieldErrors.newPassword ? 'border-red-400 ring-2 ring-red-500/20' : 'border-white/20 focus:border-accent focus:ring-4 focus:ring-accent/20'
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors p-1"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {fieldErrors.newPassword && <span className="text-[11px] font-bold text-red-300 normal-case">{fieldErrors.newPassword}</span>}
            </label>
            <button disabled={loading} className="btn-flash mt-2 w-full justify-center bg-cta hover:bg-cta-hover text-white font-extrabold text-sm py-4 px-6 rounded-2xl flex items-center gap-2 glow-cta transition-all border-0 shadow-lg cursor-pointer disabled:opacity-70 disabled:cursor-wait" type="submit">
              {loading ? 'Resetting...' : 'Reset Password'} <span>→</span>
            </button>
            <button type="button" onClick={() => {
              setStep(1);
              setMessage('');
              setError('');
              setOtp('');
              setNewPassword('');
              setFieldErrors({});
            }} className="text-center text-xs text-white/70 hover:text-white mt-2 transition-colors bg-transparent border-none cursor-pointer">Back</button>
          </form>
        )}
      </div>
    </main>
  );
}
